import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, Warning, SpinnerGap } from "@phosphor-icons/react";

export default function FinishSignup() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { applyAuth } = useAuth();
  const [state, setState] = useState("loading"); // loading | ready | done | error | already
  const [info, setInfo] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) { setState("error"); setMsg("This link is missing its verification code."); return; }
      try {
        const { data } = await api.get(`/auth/finish-signup/info?token=${encodeURIComponent(token)}`);
        if (!alive) return;
        setInfo(data);
        if (data.already_active) { setState("already"); }
        else { setState("ready"); }
      } catch (e) {
        if (!alive) return;
        setState("error"); setMsg(apiError(e));
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/finish-signup/complete", { token });
      applyAuth(data);
      setState("done");
      setTimeout(() => navigate("/welcome"), 1400);
    } catch (e) { setMsg(apiError(e)); setState("error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-[#070B16] px-6 text-white" data-testid="finish-signup-page">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0d1526] p-8 text-center shadow-xl">
        {state === "loading" && (
          <div className="flex flex-col items-center gap-3 text-slate-300" data-testid="finish-loading">
            <SpinnerGap size={34} className="animate-spin text-[#FF7A00]" />
            <p>Checking your verification link…</p>
          </div>
        )}

        {state === "ready" && (
          <div data-testid="finish-ready">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-[#FF7A00]/15">
              <CheckCircle size={30} weight="fill" className="text-[#FF7A00]" />
            </div>
            <h1 className="font-display text-2xl font-semibold">Confirm it's you</h1>
            <p className="mt-2 text-sm text-slate-300">
              {info?.name ? `Hi ${info.name.split(" ")[0]}, ` : ""}activate the account for{" "}
              <span className="font-semibold text-white">{info?.email}</span>. You'll sign in with the
              password you chose when you signed up.
            </p>
            <button data-testid="finish-verify-btn" onClick={complete} disabled={busy}
              className="mt-6 w-full rounded-full bg-[#FF7A00] py-3 font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60">
              {busy ? "Activating…" : "Verify & activate my account"}
            </button>
          </div>
        )}

        {state === "done" && (
          <div data-testid="finish-done">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-emerald-500/15">
              <CheckCircle size={30} weight="fill" className="text-emerald-400" />
            </div>
            <h1 className="font-display text-2xl font-semibold">You're all set 🎉</h1>
            <p className="mt-2 text-sm text-slate-300">Your account is live. Taking you in…</p>
          </div>
        )}

        {state === "already" && (
          <div data-testid="finish-already">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-emerald-500/15">
              <CheckCircle size={30} weight="fill" className="text-emerald-400" />
            </div>
            <h1 className="font-display text-2xl font-semibold">Account already active</h1>
            <p className="mt-2 text-sm text-slate-300">
              Your account for <span className="font-semibold text-white">{info?.email}</span> is already set up.
            </p>
            <Link to="/login" data-testid="finish-login-link"
              className="mt-6 inline-block w-full rounded-full bg-[#FF7A00] py-3 font-semibold text-white hover:opacity-90">
              Sign in
            </Link>
          </div>
        )}

        {state === "error" && (
          <div data-testid="finish-error">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-red-500/15">
              <Warning size={30} weight="fill" className="text-red-400" />
            </div>
            <h1 className="font-display text-2xl font-semibold">Link problem</h1>
            <p className="mt-2 text-sm text-red-300">{msg || "This link is no longer valid."}</p>
            <Link to="/register" data-testid="finish-register-link"
              className="mt-6 inline-block w-full rounded-full border border-white/15 py-3 font-semibold text-white hover:bg-white/5">
              Start sign-up again
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
